import React, { useEffect, useState } from "react";
import clsx from "clsx";
import {
  Box,
  makeStyles,
  Button,
  Typography,
  IconButton,
  Checkbox,
  Divider
} from "@material-ui/core";
import SmallSearchBox from "../common/SmallSearchBox";
import { Link as RouterLink } from "react-router-dom";
import NoteAddOutlinedIcon from "@material-ui/icons/NoteAddOutlined";
import { isEmptyObj, downloadStops, makeTextPlural } from "../../utils/helpers";
import { setInfoDialog } from "../../store/actions/common";
import { connect } from "react-redux";
import DeleteIcon from "@material-ui/icons/Delete";
import SaveAltIcon from "@material-ui/icons/SaveAlt";
import RestoreIcon from "@material-ui/icons/Restore";
import OptimizedTooltip from "../common/OptimizedTooltip";
// import MoveIcon from "../icons/MoveIcon";
// import EditIcon from "@material-ui/icons/Edit";
// import CreateRouteIcon from "../icons/CreateRouteIcon";
import { deleteStops, reopenStops } from "../../store/actions/custom";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import useProductTour, {
  PRODUCT_TOUR_STEP_IDS
} from "../../states/tour/tours/product";

const useStyles = makeStyles(theme => ({
  toolbarWrapper: {
    height: 40,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2, 2, 2, 3),

    "& > div": {
      display: "flex",
      alignItems: "center"
    }
  },
  button: {
    marginLeft: theme.spacing(1.5)
  },
  iconButton: {
    "&.Mui-disabled": {
      opacity: 0.5,
      pointerEvents: "all",
      cursor: theme.cursors.notAllowed
    }
  },
  searchBox: {
    height: 40,
    transition: "width 0.3s",
    marginLeft: 0,
    width: ({ focusedOuter, isEditMode, isPublished }) =>
      !focusedOuter ? (!isEditMode || !isPublished ? "100%" : 300) : "100%"
  },
  importButton: {
    marginLeft: theme.spacing(1.5),
    textTransform: "none",
    whiteSpace: "nowrap",
    fontSize: theme.typography.pxToRem(15),
    padding: theme.spacing(0.75, 2, 0.75, 1.5),
    border: "1px solid #DBDCE1",
    "& svg": {
      color: theme.palette.primary.main,
      marginRight: theme.spacing(1)
    }
  },
  importButtonDisabled: {
    "& button": {
      cursor: [theme.cursors.notAllowed, "!important"]
    }
  },
  toolbarActions: {
    minHeight: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    marginBottom: theme.spacing(2)
  },
  checkboxWrapper: {
    display: "flex",
    alignItems: "center",
    marginLeft: theme.spacing(-1),
    "& .MuiSvgIcon-root": {
      color: theme.palette.primary.main
    }
  },
  selectedText: {
    marginLeft: theme.spacing(1),
    color: theme.palette.primary.main,
    fontWeight: 500,
    fontSize: "0.875rem",
    lineHeight: "1rem"
  }
}));

function StopsToolbar({
  projectExpired,
  saveDisabled,
  organisation,
  stops,
  stopsLength,
  solution,
  stopsCount,
  selectedStops,
  searchInputValue,
  setSearchInputValue,
  setChangesDialog,
  setInfoDialog,
  numSelected,
  allStopsSelected,
  handleSelectAllClick,
  deleteStops,
  reopenStops,
  clearAllSelectedStops,
  isEditMode,
  isPublished
}) {
  const [focusedOuter, setFocusedOuter] = useState(false);

  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [reopenConfirmation, setReopenConfirmation] = useState(false);

  const { plan } = organisation;

  const classes = useStyles({ focusedOuter, isEditMode, isPublished });
  const handleSearchChange = searchInputValue => {
    setSearchInputValue(searchInputValue);
  };

  const [isLockedInSelection, setIsLockedInSelection] = useState(false);

  useEffect(() => {
    const lockedRoutes = solution
      ? solution.routes.filter(route => route.locked)
      : [];

    setIsLockedInSelection(
      lockedRoutes.some(route =>
        route.stops.some(stop => selectedStops.scheduled.includes(stop.id))
      )
    );
  }, [selectedStops]);

  // Create flat array from selectedStops for more confortable usage;
  const allStops = Object.values(stops).flat();
  const allSelectedStops = allStops.filter(
    stop =>
      selectedStops.unscheduled.includes(stop.id) ||
      selectedStops.scheduled.includes(stop.id)
  );

  const handleDownloadStops = stops => downloadStops(stops);

  const handleDeleteStops = stops => {
    deleteStops(stops);
    clearAllSelectedStops();
  };

  const stopsSearchboxPlaceholder =
    isPublished && !isEditMode ? "Search stops" : "Add or search stops";

  const reopenableStopIds = allSelectedStops
    .filter(stop => stop && (stop.status === "finished" || stop.status === "skipped"))
    .map(stop => stop.id);
  const handleReopenStops = () => {
    if (reopenStops) reopenStops(reopenableStopIds);
  };

  const productTour = useProductTour();
  const isProductTourPartAddingStopsAndOptimizeStepBrief =
    productTour.isTourStepId(
      PRODUCT_TOUR_STEP_IDS.PART_ADDING_STOPS_AND_OPTIMIZE_STEP_BRIEF
    );

  return (
    <>
      <div className={classes.toolbarWrapper}>
        <Box overflow="hidden" width="100%">
          <SmallSearchBox
            isEnhancedSearch
            placeholder={stopsSearchboxPlaceholder}
            showSearchField={true}
            focusedOuter={focusedOuter}
            setFocusedOuter={focused => {
              if (isProductTourPartAddingStopsAndOptimizeStepBrief) {
                productTour.goToNext();
              }
              setFocusedOuter(focused);
            }}
            className={classes.searchBox}
            searchText={searchInputValue}
            onSearchChanged={handleSearchChange}
            ariaLabel={stopsSearchboxPlaceholder}
            dataCy="stops-search"
            id="add_stops_search_box"
          />
        </Box>
        {!projectExpired && !focusedOuter && (isEditMode || !isPublished) && (
          <RouterLink
            onClick={e => {
              if (productTour.hasActiveTourCurrently()) {
                e.preventDefault();
                return;
              }
              // Check if upload is allowed or if stops limit reached -> if yes show dialog, else go to upload stops
              if (plan.ft_spreadsheet_upload) {
                if (plan.cap_stops_per_project <= stopsCount) {
                  e.preventDefault();
                  setInfoDialog({
                    visible: true,
                    title: "Operation not possible",
                    body: `You’ve reached the maximum number of stops that you can add to one project. If you want to add more than ${plan.cap_stops_per_project} stops per project you need to upgrade your plan.`,
                    button: {
                      text: "Upgrade plan",
                      link: "/payment/select-plan"
                    },
                    mainButtonText: "No, thanks"
                  });
                  return;
                }

                if (!saveDisabled) {
                  e.preventDefault();
                  setChangesDialog({ visible: true });
                }
              } else {
                e.preventDefault();
                setInfoDialog({
                  visible: true,
                  title: "Please upgrade your plan",
                  body: "Spreadsheet importing is not included in the Individual plan, please upgrade your plan to Small Business.",
                  button: {
                    text: "Upgrade plan",
                    link: "/payment/select-plan"
                  },
                  mainButtonText: "No, thanks"
                });
              }
            }}
            to={location => `${location.pathname}/upload-stops`}
            style={{ textDecoration: "none" }}
            className={clsx(
              productTour.hasActiveTourCurrently() &&
                classes.importButtonDisabled
            )}
          >
            <Button
              className={classes.importButton}
              color="primary"
              data-cy="uploadStops-btn"
            >
              <NoteAddOutlinedIcon />
              {stopsLength ? "Import" : "Import stops"}
            </Button>
          </RouterLink>
        )}

        {/* {!!stopsLength && (
          <IconButton
            className={clsx(classes.iconButton, classes.button)}
            variant="outlined"
          >
            <img src={filterIcon} alt="" />
          </IconButton>
        )} */}
      </div>
      {!!numSelected && (
        <div className={classes.toolbarActions}>
          <div className={classes.checkboxWrapper}>
            <OptimizedTooltip
              title={
                allStopsSelected ? (
                  "Deselect all"
                ) : (
                  <>
                    Select all
                    {/* <span
                      className={classes.chip}
                      style={{ marginRight: "2px", marginLeft: "6px" }}
                    >
                      ⌘
                    </span>
                    <span className={classes.chip}>A</span> */}
                  </>
                )
              }
              placement="bottom"
            >
              <Checkbox
                color="primary"
                indeterminate={
                  numSelected > 0 &&
                  numSelected < stopsLength &&
                  !allStopsSelected
                }
                checked={!!allStopsSelected}
                onChange={handleSelectAllClick}
              />
            </OptimizedTooltip>
            <Typography className={classes.selectedText}>
              {numSelected} selected
            </Typography>
          </div>
          <Box display={"flex"} alignItems={"center"}>
            {/* Don't delete this for v1.16
            <OptimizedTooltip
            //   placement="bottom"
            //   title={
            //     isLockedInSelection
            //       ? "Unable to create routes with locked stops"
            //       : "Create new route"
            //   }


            // >
            //   <span>
            //     <IconButton
            //       className={classes.iconButton}
            //       disabled={isLockedInSelection}
            //       onClick={() => {}}
            //     >
            //       <CreateRouteIcon />
            //     </IconButton>
            //   </span>
            // </OptimizedTooltip>
            // <OptimizedTooltip
            //   placement="bottom"
            //   title={
            //     isLockedInSelection ? "Unable to move locked stops" : "Move to"
            //   }
            // >
            //   <span>
            //     <IconButton
            //       className={classes.iconButton}
            //       disabled={isLockedInSelection}
            //       onClick={() => {}}
            //     >
            //       <MoveIcon />
            //     </IconButton>
            //   </span>
            // </OptimizedTooltip>
            // <Divider
            //   style={{ height: 24, margin: "0 4px" }}
            //   variant="vertical"
            // /> _
            */}
            <OptimizedTooltip
              placement="bottom"
              title={
                reopenableStopIds.length
                  ? `Reopen stop${makeTextPlural(reopenableStopIds.length)}`
                  : "Select completed or skipped stops to reopen"
              }
            >
              <span>
                <IconButton
                  className={classes.iconButton}
                  data-id="stop_toolbar_reopen_button"
                  disabled={!reopenableStopIds.length}
                  onClick={() =>
                    !reopenConfirmation && setReopenConfirmation(true)
                  }
                >
                  <RestoreIcon />
                </IconButton>
              </span>
            </OptimizedTooltip>

            <OptimizedTooltip
              placement="bottom"
              title={`Download stop${makeTextPlural(allSelectedStops.length)}`}
            >
              <IconButton
                onClick={() => handleDownloadStops(allSelectedStops)}
                className={classes.iconButton}
                data-id="stop_toolbar_download_button"
              >
                <SaveAltIcon />
              </IconButton>
            </OptimizedTooltip>
            {/* v-1.16+ <
            OptimizedTooltip
              placement="bottom"
              title={
                isLockedInSelection ? "Unable to edit locked stops" : "Edit"
              }
            >
              <IconButton className={classes.iconButton} onClick={() => {}}>
                <EditIcon />
              </IconButton>
            </OptimizedTooltip> */}
            {(isEditMode || !isPublished) && !projectExpired && (
              <OptimizedTooltip
                placement="bottom"
                title={
                  isLockedInSelection
                    ? "Unable to delete locked stops"
                    : `Delete stop${makeTextPlural(allSelectedStops.length)}`
                }
              >
                <span>
                  <IconButton
                    className={classes.iconButton}
                    data-id="stop_toolbar_delete_button"
                    disabled={isLockedInSelection}
                    onClick={e =>
                      !deleteConfirmation && setDeleteConfirmation(true)
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </span>
              </OptimizedTooltip>
            )}
          </Box>
        </div>
      )}
      <ConfirmDialog
        isProject
        open={deleteConfirmation}
        handleClose={() => setDeleteConfirmation(false)}
        action={() => {
          // Make selectedStops Object flat array for easy usage
          const selectedStopsFlat = Object.values(selectedStops).flat();
          handleDeleteStops(selectedStopsFlat);
        }}
        title={`Delete ${
          allSelectedStops.length === 1 ? "a" : ""
        } stop${makeTextPlural(allSelectedStops.length)}?`}
        body={`Are you sure you want to delete ${
          allSelectedStops.length > 1 ? allSelectedStops.length : "a"
        } stop${makeTextPlural(allSelectedStops.length)}?`}
      />
      <ConfirmDialog
        isProject
        open={reopenConfirmation}
        handleClose={() => setReopenConfirmation(false)}
        action={handleReopenStops}
        confirmText="Reopen"
        title={`Reopen ${reopenableStopIds.length} stop${makeTextPlural(
          reopenableStopIds.length
        )}?`}
        body={`This resets the selected completed/skipped stop${makeTextPlural(
          reopenableStopIds.length
        )} to to-do, clearing proof of delivery, photos and completion time, and recalculating route times. Customers are not notified.`}
      />
    </>
  );
}

const mapStateToProps = state => ({
  projectExpired: ["expired", "finished"].includes(
    state.project.status.toLowerCase()
  ),
  isEditMode: state.project.status === "optimized",
  isPublished: state.project.published_once,
  billingDetails: state.account.billingDetails,
  saveDisabled:
    state.extendable.saveDisabled && state.common.settingsSaveDisabled,
  organisation:
    (state.auth.userData.organisationSite &&
      state.auth.userData.organisationSite.organisation) ||
    {},
  solution: state.project.solution,
  stopsCount: state.project.vrp.stops.filter(
    task =>
      task.type !== "start" &&
      task.type !== "end" &&
      !isEmptyObj(task) &&
      task.type !== "break"
  ).length
});

export default connect(mapStateToProps, { setInfoDialog, deleteStops, reopenStops })(
  StopsToolbar
);
